import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FaGamepad, FaBullseye } from "react-icons/fa";
import { OWGameResponse } from "@devour/client";
import { useGameSearchInfinite } from "@/hooks/overwolf/useGameSearchInfinite";
import LazyImage from "./LazyImage";
import "./GameQuestSuggestions.scss";

interface GameQuestSuggestionsProps {
    currentGameId?: string;
    maxGames?: number;
}

const GameQuestSuggestions: React.FC<GameQuestSuggestionsProps> = ({
    currentGameId,
    maxGames = 6,
}) => {
    const navigate = useNavigate();

    // Use infinite scroll hook but only for games with events
    const { games, isLoading } = useGameSearchInfinite({
        category: "events", // Only games with events supported
    });

    // Filter for games with good imagery (events already filtered server-side)
    const questGames = useMemo(() => {
        if (!games) return [];

        return games
            .filter(game => {
                // Skip current game
                if (game.gameId === currentGameId) return false;
                // Must have some image (prefer capsule/header over logo)
                const steamData = (game as any).steamData || {};
                if (!steamData.capsuleImage && !steamData.headerImage && !game.steamLogoUrl) return false;
                return true;
            })
            .sort((a, b) => {
                // First prioritize games with capsule images
                const aHasCapsule = !!(a as any).steamData?.capsuleImage;
                const bHasCapsule = !!(b as any).steamData?.capsuleImage;

                if (aHasCapsule && !bHasCapsule) return -1;
                if (!aHasCapsule && bHasCapsule) return 1;

                // Then popular games
                const popularGames = ["counter-strike-2", "overwatch-2", "rocket-league", "marvel-rivals", "valorant", "league-of-legends"];
                const aIndex = popularGames.indexOf(a.slug || "");
                const bIndex = popularGames.indexOf(b.slug || "");

                if (aIndex !== -1 && bIndex !== -1) {
                    return aIndex - bIndex;
                }
                if (aIndex !== -1) return -1;
                if (bIndex !== -1) return 1;

                // Then alphabetically
                return (a.name || "").localeCompare(b.name || "");
            })
            .slice(0, maxGames);
    }, [games, currentGameId, maxGames]);

    const handleGameClick = (game: OWGameResponse) => {
        if (game.slug) {
            navigate(`/games/${game.slug}`);
        }
    };

    if (isLoading) {
        return (
            <div className="game-quest-suggestions game-quest-suggestions--loading">
                <div className="game-quest-suggestions__spinner" />
                <p>Loading quest-enabled games...</p>
            </div>
        );
    }

    if (!questGames.length) {
        return null;
    }

    return (
        <div className="game-quest-suggestions">
            <div className="game-quest-suggestions__header">
                <FaBullseye className="game-quest-suggestions__icon" />
                <h3>Games with Active Quests</h3>
            </div>
            <div className="game-quest-suggestions__grid">
                {questGames.map(game =>
                    <div
                        key={game.gameId || game._id}
                        className="game-quest-suggestions__card"
                        onClick={() => handleGameClick(game)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                handleGameClick(game);
                            }
                        }}
                    >
                        <div className="game-quest-suggestions__card-image">
                            <LazyImage
                                src={(game as any).steamData?.capsuleImage ||
                                     (game as any).steamData?.headerImage ||
                                     game.steamLogoUrl ||
                                     game.logo}
                                alt={game.name}
                                fallbackIcon={<FaGamepad />}
                            />
                        </div>
                        <div className="game-quest-suggestions__card-content">
                            <h4>{game.name}</h4>
                            <button
                                className="game-quest-suggestions__card-button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleGameClick(game);
                                }}
                            >
                                View Quests
                            </button>
                        </div>
                    </div>)}
            </div>
            <button
                className="game-quest-suggestions__explore"
                onClick={() => {
                    // Open search modal filtered to quest games
                    const searchButton = document.querySelector(".game-quest-section__link") as HTMLButtonElement;
                    if (searchButton) {
                        searchButton.click();
                        // After modal opens, click the quest filter
                        setTimeout(() => {
                            const questFilter = document.querySelector(".game-search-modal__filter-btn[title*=\"quest\"]") as HTMLButtonElement;
                            if (questFilter) {
                                questFilter.click();
                            }
                        }, 100);
                    }
                }}
            >
                Explore All Quest Games →
            </button>
        </div>
    );
};

export default GameQuestSuggestions;